import type {
  Material,
  ClaudeModel,
  StudioSession,
  EffortLevel,
  AgentRun,
  AuthMode,
  Decision,
  DecisionBinding,
  DeliveryMode,
  DomainEvent,
  EfficiencyReport,
  Idea,
  OwnerTask,
  OwnerTaskPlace,
  PlanContent,
  Platform,
  Project,
  ProjectLifecycle,
  Role,
  RolloutView,
  DeployAgent,
  ProjectRolloutView,
  SpecContent,
  SzyNode,
  WorkItem,
  WorkOrder,
} from "@szyos/contracts";
import type { LiveStage, StocktakeDoc, UsageOverview } from "@szyos/domain";
import type { ApprovalEntry } from "./components/ApprovalQueue";

/** Ergebnis der letzten Bestandsaufnahme eines Projekts (apps/server/src/services/stocktake.ts). */
export interface StocktakeView {
  orderId: string | null;
  orderState: string | null;
  prUrl: string | null;
  source: "branch" | "main" | null;
  markdown: string | null;
  doc: StocktakeDoc | null;
  reason: string | null;
}

declare const __SZYOS_BUILD__: string;

/** Build-Kennung dieses Studios; der Server weist ändernde Anfragen einer veralteten Version ab. */
export const STUDIO_BUILD: string = __SZYOS_BUILD__;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: unknown = null,
  ) {
    super(message);
  }
}

async function request<T>(method: "GET" | "POST", path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: "same-origin",
      headers: {
        "x-szyos-build": STUDIO_BUILD,
        ...(body === undefined ? {} : { "content-type": "application/json" }),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, "NETWORK", "Server nicht erreichbar. Verbindung prüfen.");
  }
  const text = await res.text();
  const json = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const err = (
      json as { error?: { code?: string; message?: string; details?: unknown; issues?: unknown } } | null
    )?.error;
    throw new ApiError(
      res.status,
      err?.code ?? "HTTP",
      err?.message ?? `Fehler ${res.status}`,
      err?.details ?? err?.issues ?? null,
    );
  }
  return json as T;
}

export const get = <T>(path: string) => request<T>("GET", path);
export const post = <T>(path: string, body: unknown = {}) => request<T>("POST", path, body);

export interface AuthStatus {
  authenticated: boolean;
  user: { id: string; displayName: string; role: Role } | null;
  setupRequired: boolean;
  setupEnabled: boolean;
}

export interface WorkOrderSummary extends WorkOrder {
  projectTitle: string;
  itemsDone: number;
  itemsTotal: number;
  openDecisions: number;
  /** Aktive Läufe (Arbeit und Planvorschläge): nur sie belegen, dass gerade etwas passiert. */
  activeRuns: number;
  queuedRuns: number;
  /** Offene Schritte bei dir; erst danach ist ein ausgelieferter Auftrag wirklich fertig. */
  openOwnerSteps: number;
}

export interface WorkOrderDetail {
  workOrder: WorkOrder;
  project: Project;
  suggestedSpec: SpecContent | null;
  spec: { revision: number; hash: string; content: SpecContent; approvedAt: string | null } | null;
  plan: { revision: number; hash: string; content: PlanContent; approvedAt: string | null } | null;
  items: WorkItem[];
  runs: AgentRun[];
  decisions: Decision[];
  cost: { measuredUsd: number; estimatedUsd: number; budgetUsd: number };
  /** Herkunft aus taskFLOW mit Link zur Task (docs/08). */
  taskflow: { remoteUrl: string; requestedBy: string } | null;
  /** Ein Planvorschlag oder eine Klärung des Auftrags läuft gerade im Studio. */
  studioActive: boolean;
  openOwnerSteps: number;
  /** Stufe „Live“: echter Stand nach dem Merge (packages/domain/src/live.ts). */
  live: LiveStage;
}

/** Kosten heute je Adapter und Auth-Modus (`GET /api/v1/costs/today`). */
export interface CostRow {
  adapter: string;
  /** `subscription` steht für Abo-Läufe ohne Dollarbetrag. */
  authMode?: AuthMode;
  measuredUsd: number;
  estimatedUsd: number;
  quotaUnits: number;
  /** Abo-Läufe: geschätzter API-Gegenwert, nur Anzeige, nie Teil der Summe (docs/03 §8). */
  apiEquivalentUsd?: number;
  runs: number;
}

/** Owner-Modus nach ADR-016: Abo des Owners und API-Reserve, Zugangsdaten kennt der Server nie. */
export interface ProviderAuthStatus {
  subscriptionEnabled: boolean;
  /** Der angemeldete Mensch ist Inhaber des eingeschalteten Abos. */
  subscriptionIsMine: boolean;
  apiKeyEnabled: boolean;
  subscriptionPausedUntil: string | null;
  nodesWithSubscription: number;
  nodesWithApiKey: number;
}

export const api = {
  authStatus: () => get<AuthStatus>("/api/v1/auth/status"),
  logout: () => post("/api/v1/auth/logout"),
  ideas: () => get<Idea[]>("/api/v1/ideas"),
  captureIdea: (input: { title: string; body?: string; tags?: string[]; origin?: "web" | "mobile" }) =>
    post<Idea>("/api/v1/ideas", input),
  updateIdea: (
    id: string,
    input: { title: string; body: string; tags: string[]; expectedUpdatedAt: string },
  ) => post<Idea>(`/api/v1/ideas/${id}/content`, input),
  setProjectLifecycle: (id: string, lifecycle: ProjectLifecycle) =>
    post<Project>(`/api/v1/projects/${id}/lifecycle`, { lifecycle }),
  updateProject: (id: string, input: { title: string; goal: string }) =>
    post<Project>(`/api/v1/projects/${id}/content`, input),
  setIdeaMaturity: (id: string, maturity: "RAW" | "CLARIFIED" | "READY" | "PARKED" | "DISCARDED") =>
    post<Idea>(`/api/v1/ideas/${id}/maturity`, { maturity }),
  promoteIdea: (
    id: string,
    input: { profileKey: string; platform: Platform; focus?: boolean; pauseProjectId?: string },
  ) => post<Project>(`/api/v1/ideas/${id}/promote`, input),
  setProjectFocus: (id: string, focus: boolean, pauseProjectId?: string) =>
    post<Project>(`/api/v1/projects/${id}/focus`, pauseProjectId ? { focus, pauseProjectId } : { focus }),
  setProjectDone: (id: string, input: { criteria: { text: string; met: boolean }[]; later: string[] }) =>
    post<Project>(`/api/v1/projects/${id}/done`, input),
  setProjectDelivery: (id: string, input: { mode: DeliveryMode; steps: string[] }) =>
    post<Project>(`/api/v1/projects/${id}/delivery`, input),
  stocktake: (id: string) => get<StocktakeView>(`/api/v1/projects/${id}/stocktake`),
  orderFromStocktake: (id: string) => post<WorkOrder>(`/api/v1/projects/${id}/stocktake/next-order`),
  startStocktake: (id: string) => post<WorkOrder>(`/api/v1/projects/${id}/stocktake`),
  weeklyReview: () => get<{ lastReviewAt: string | null }>("/api/v1/reviews/weekly"),
  approvals: () => get<ApprovalEntry[]>("/api/v1/approvals"),
  ownerTasks: () => get<OwnerTask[]>("/api/v1/owner-tasks"),
  createOwnerTask: (input: {
    title: string;
    place: OwnerTaskPlace;
    urgent: boolean;
    projectId: string | null;
    steps: string[];
  }) => post<OwnerTask>("/api/v1/owner-tasks", input),
  setOwnerTaskDone: (id: string, done: boolean) =>
    post<OwnerTask>(`/api/v1/owner-tasks/${id}/done`, { done }),
  completeWeeklyReview: () => post<{ lastReviewAt: string }>("/api/v1/reviews/weekly"),
  rollout: () => get<RolloutView>("/api/v1/rollout"),
  requestRollout: (sha: string) => post<RolloutView>("/api/v1/rollout", { sha }),
  deployAgents: () => get<DeployAgent[]>("/api/v1/deploy-agents"),
  createDeployAgentCode: () => post<{ code: string; expiresAt: string }>("/api/v1/deploy-agents/codes"),
  projectRollout: (id: string) => get<ProjectRolloutView>(`/api/v1/projects/${id}/rollout`),
  requestProjectRollout: (id: string, sha: string) =>
    post<ProjectRolloutView>(`/api/v1/projects/${id}/rollout`, { sha }),
  setDeployTarget: (id: string, input: { agentId: string | null; app: string | null }) =>
    post<Project>(`/api/v1/projects/${id}/deploy-target`, input),
  health: () => get<{ ok: boolean; build: string; simulation?: boolean }>("/api/v1/health"),
  projects: () => get<Project[]>("/api/v1/projects"),
  setProjectRepository: (id: string, fullName: string | null) =>
    post<Project>(`/api/v1/projects/${id}/repository`, { fullName }),
  createProject: (input: { title: string; goal: string; platform: Platform; profileKey: string }) =>
    post<Project>("/api/v1/projects", input),
  project: (id: string) => get<Project>(`/api/v1/projects/${id}`),
  workOrders: () => get<WorkOrderSummary[]>("/api/v1/work-orders"),
  createWorkOrder: (projectId: string, input: { title: string; kind?: string; budgetUsd?: number }) =>
    post<WorkOrder>(`/api/v1/projects/${projectId}/work-orders`, input),
  workOrder: (id: string) => get<WorkOrderDetail>(`/api/v1/work-orders/${id}`),
  workOrderEvents: (id: string) => get<DomainEvent[]>(`/api/v1/work-orders/${id}/events`),
  recentEvents: () => get<DomainEvent[]>("/api/v1/events/recent"),
  submitSpec: (id: string, spec: SpecContent, expectedVersion: number) =>
    post<WorkOrder>(`/api/v1/work-orders/${id}/spec`, { spec, expectedVersion }),
  splitWorkOrder: (
    id: string,
    input: { title: string; mustHaves: number[]; acceptance?: number[]; expectedVersion: number },
  ) => post<{ workOrder: WorkOrder; created: WorkOrder }>(`/api/v1/work-orders/${id}/split`, input),
  approveSpec: (id: string, approvedHash: string, expectedVersion: number) =>
    post<WorkOrder>(`/api/v1/work-orders/${id}/approve-spec`, { approvedHash, expectedVersion }),
  submitPlan: (id: string, plan: PlanContent, expectedVersion: number) =>
    post<WorkOrder>(`/api/v1/work-orders/${id}/plan`, { plan, expectedVersion }),
  approvePlan: (id: string, approvedHash: string, expectedVersion: number) =>
    post<WorkOrder>(`/api/v1/work-orders/${id}/approve-plan`, { approvedHash, expectedVersion }),
  start: (id: string, expectedVersion: number) =>
    post<WorkOrder>(`/api/v1/work-orders/${id}/start`, { expectedVersion }),
  cancel: (id: string, reason: string) => post<WorkOrder>(`/api/v1/work-orders/${id}/cancel`, { reason }),
  markAlreadyDone: (id: string, note = "") =>
    post<WorkOrder>(`/api/v1/work-orders/${id}/already-done`, { note }),
  holdWorkOrder: (id: string, hold: boolean) => post<WorkOrder>(`/api/v1/work-orders/${id}/hold`, { hold }),
  prioritizeWorkOrder: (id: string) => post<WorkOrder>(`/api/v1/work-orders/${id}/prioritize`),
  decisions: (state?: string) => get<Decision[]>(`/api/v1/decisions${state ? `?state=${state}` : ""}`),
  resolveDecision: (id: string, optionId: string, binding: DecisionBinding, checked?: number[]) =>
    post<Decision>(
      `/api/v1/decisions/${id}/resolve`,
      checked ? { optionId, binding, checked } : { optionId, binding },
    ),
  costsToday: () => get<CostRow[]>("/api/v1/costs/today"),
  usage: () => get<UsageOverview>("/api/v1/usage"),
  efficiency: () => get<EfficiencyReport>("/api/v1/efficiency"),
  killSwitch: () => get<{ active: boolean }>("/api/v1/killswitch"),
  triggerKillSwitch: (reason: string) =>
    post<{ cancelledRuns: number }>("/api/v1/killswitch", { scope: "workspace", reason }),
  releaseKillSwitch: () => post("/api/v1/killswitch/release"),
  nodes: () => get<SzyNode[]>("/api/v1/nodes"),
  createNodeCode: () => post<{ code: string; expiresAt: string }>("/api/v1/nodes/registration-codes"),
  revokeNode: (id: string) => post(`/api/v1/nodes/${id}/revoke`),
  studioSessions: (query: { ideaId?: string; workOrderId?: string }) =>
    get<StudioSession[]>(`/api/v1/studio?${new URLSearchParams(query as Record<string, string>).toString()}`),
  startClarification: (ideaId: string) => post<StudioSession>(`/api/v1/ideas/${ideaId}/studio`),
  recheckStudio: (sessionId: string) => post<StudioSession>(`/api/v1/studio/${sessionId}/recheck`),
  startPlanDraft: (workOrderId: string) =>
    post<StudioSession>(`/api/v1/work-orders/${workOrderId}/plan-draft`),
  answerStudio: (sessionId: string, text: string) =>
    post<StudioSession>(`/api/v1/studio/${sessionId}/answer`, { text }),
  agentSettings: () => get<{ model: ClaudeModel; effort: EffortLevel }>("/api/v1/agent-settings"),
  setAgentSettings: (input: { model: ClaudeModel; effort: EffortLevel }) =>
    post<{ model: ClaudeModel; effort: EffortLevel }>("/api/v1/agent-settings", input),
  providerAuth: () => get<ProviderAuthStatus>("/api/v1/provider-auth"),
  setProviderAuth: (input: { subscription: boolean; apiKey: boolean }) =>
    post<ProviderAuthStatus>("/api/v1/provider-auth", input),
  material: (owner: MaterialOwnerRef) => get<Material[]>(`/api/v1/${owner.type}/${owner.id}/material`),
  addMaterial: (owner: MaterialOwnerRef, input: NewMaterial) =>
    post<Material>(`/api/v1/${owner.type}/${owner.id}/material`, input),
  updateMaterial: (id: string, input: { note?: string; inRepo?: boolean }) =>
    post<Material>(`/api/v1/material/${id}`, input),
  deleteMaterial: (id: string) => post(`/api/v1/material/${id}/delete`, {}),
  materialToRepo: (projectId: string) =>
    post<{ prUrl: string; files: number }>(`/api/v1/projects/${projectId}/material/repo`, {}),
};

/** Wem das Material gehört: einer Idee oder einem Projekt (Pfadteil der API). */
export interface MaterialOwnerRef {
  type: "ideas" | "projects";
  id: string;
}

export type NewMaterial =
  | { kind: "note"; title: string; text: string }
  | { kind: "link"; title: string; url: string; note: string }
  | { kind: "file"; fileName: string; contentBase64: string; note: string };

/** Adresse einer Materialdatei für Vorschau und Download. */
export const materialFileUrl = (id: string): string => `/api/v1/material/${id}/file`;

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return "Unerwarteter Fehler. Bitte erneut versuchen.";
}
